/**
 * What `GET /api/profile` and `PUT /api/profile` return — the signed-in user plus the organization
 * context the app gates on.
 *
 * These fields are the ones `app/composables/useUserProfile.ts` declares as `interface UserProfile`.
 * They are declared here so the SERVICE is checked against them too: `getProfileByFirebaseUid` ran a
 * completely untyped `pool.query`, so its `rows[0]` was `any` and the whole response shape was
 * unchecked from the projection all the way to the composable.
 *
 * The `org_override_*` trio is not from the database at all — it is the superadmin impersonation the
 * request context carries, added by `buildUserProfile`. `PUT /api/profile` re-assembles the same
 * shape by hand, deliberately: a partial response there would drop the org and misgate onboarding.
 */
import type { PlanFeature, PlanFeatureFlags } from '../organizations/planFeatures'
import type { SecondPerson } from './secondPerson'
import type { OnboardingProfileState } from '../onboarding/flow'

/**
 * The longest `display_name` `PUT /api/profile` accepts. Shared so the setup page can refuse a longer
 * one in the user's own language before sending it, instead of showing the server's "Validation error"
 * (MAR-933, tester F6).
 */
export const DISPLAY_NAME_MAX_LENGTH = 200

interface UserRowFields<D> {
  id: string
  firebase_uid: string
  email: string
  display_name: string | null
  /**
   * How the user wants to be addressed in Spanish. NOT NULL in the database with a default, so this
   * is never absent — see `shared/organizations/secondPerson.ts` for the vocabulary and why it is stored.
   */
  second_person: SecondPerson
  /** User-level PostHog product analytics & session replay consent (`MAR-1348`). `null` = unset. */
  analytics_consent: boolean | null
  is_superadmin: boolean
  created_at: D
  updated_at: D
}

interface UserProfileFields<D> extends UserRowFields<D> {
  /** `null` when the user has no organization yet — the state onboarding exists to resolve. */
  organization_name: string | null
  organization_main_jurisdiction: string | null
  organization_max_chat_tier: string | null
  /** The user's REAL org name while a superadmin is impersonating another one; `null` otherwise. */
  actual_organization_name: string | null
  /**
   * Resolved from the org's plan via `planFeatureFlags` (MAR-199) — never a raw column. Case
   * management / sheets / workflows are the only plan-gated features; anything else (chat, research,
   * templates) is always available and carries no flag here.
   */
  feature_flags: PlanFeatureFlags
  /**
   * Where the seat's plan lands the user after login and from the logo (MAR-1172): `chat`,
   * `workflows` or `laws`. `chat` for a user without a seat or an organization.
   */
  start_page: string
  /**
   * A free-trial member (MAR-1452, D19): the caller's seat in this organization is `free_tier` and the
   * organization has no active or past-due subscription — the trial organization a free user is given on their
   * first free use ("Prueba Gratis" / "Free Trial org", `shared/organizations/freeTrial.ts`). The client routes
   * them exactly like a user with no organization (`isFreeTierProfile`): browsing goes to `/chat`, buying
   * upgrades this organization. `false` for everyone else, a superadmin acting as another organization included.
   */
  free_trial: boolean
  /** The seat plan's chat allowance in tokens, `null` = unlimited or no plan. `0` means no chat (MAR-1172). */
  chat_limit: number | null
  /**
   * For each gated feature, the label of the LOWEST plan that includes it, or `null` when no plan
   * does (MAR-980, decision D2) — what the upgrade notice names at a gated route. Resolved from the
   * live plan grid (`lowestPlanWithFeature`), the same answer a plan refusal's `data.plan_label`
   * gives. Never a name kept in the client: the grid is editable.
   */
  feature_upgrade_plans: Record<PlanFeature, string | null>
  /**
   * Whether the home screen still shows its row of hint chips (#81, reworked by MAR-933).
   *
   * STORED, not derived: true until the user pressed the X (`users.onboarding_tips_dismissed_at`) —
   * or until they entered onboarding having already been active on `ONBOARDING_TIP_DATES` distinct
   * dates, which stamps the same column once. It used to be recounted from `token_usage` on every
   * profile read; that per-request count is gone.
   */
  show_tips: boolean
  /**
   * The plan-driven onboarding's state (MAR-933) — see `shared/onboarding/flow.ts` for the rules
   * that read it. `evaluation` is non-null ONLY for a user who has not entered the optional phase,
   * which is what keeps every other user's profile read free of onboarding queries.
   */
  onboarding: OnboardingProfileState
  /** Whether the user's organization requires all members to enroll in TOTP 2FA (`MAR-1181`). */
  organization_require_mfa?: boolean
  /** Whether this user has an active TOTP 2FA enrollment (`users.mfa_enrolled_at IS NOT NULL`). */
  mfa_enrolled?: boolean
}

/** Returned by `getProfileByFirebaseUid`. */
export type UserProfileRow = UserProfileFields<Date>

/** The impersonation trio `buildUserProfile` adds from the request context, never from a query. */
interface OrgOverrideFields {
  org_override_id: string | null
  org_override_name: string | null
  org_override_role: string | null
}

/** Returned by `buildUserProfile` — the full `/api/profile` shape. */
export type UserProfileWithOverrideRow = UserProfileRow & OrgOverrideFields
/** Received by the client from `GET /api/profile` and `PUT /api/profile`. */
export type UserProfile = UserProfileFields<string> & OrgOverrideFields

/**
 * What `updateProfileFields`' `RETURNING` list carries — NOT the profile shape. It omits
 * `is_superadmin`, and `PUT /api/profile` re-reads the full row afterwards for exactly that reason.
 */
export type UserProfileUpdateRow = Omit<UserRowFields<Date>, 'is_superadmin'>

/**
 * Pure client route gate for mandatory organization 2FA (`MAR-1181`).
 *
 * When `organization_require_mfa` is enabled on the user's organization and `mfa_enrolled` is not
 * true, every `/app/*` navigation other than `/app/settings/security` is redirected immediately to
 * `/app/settings/security` (no grace period), while `/app/settings/security` itself returns `null`
 * so the member can complete TOTP enrollment without a redirect loop.
 */
export function mfaRedirectTarget(
  path: string,
  profile: {
    organization_require_mfa?: boolean
    mfa_enrolled?: boolean
    org_override_id?: string | null
  } | null | undefined,
): '/app/settings/security' | null {
  const normalized = path.replace(/\/+$/, '') || '/'
  if (!normalized.startsWith('/app')) return null
  if (!profile || profile.org_override_id) return null
  if (!profile.organization_require_mfa || profile.mfa_enrolled) return null
  if (normalized === '/app/settings/security') return null
  return '/app/settings/security'
}

